import { Injectable } from "@angular/core";
import environment from "../../environments/environment";
import { formatDate } from "@angular/common";
import { MigrationDbTables, MigrationStatus } from "@app/common/constants";
import {
  Company,
  GroupedMigrationMetrics,
  Migration,
  MigrationExecutionStep,
  MigrationExecutionStoredProcedure,
  MigrationMetric,
  MigrationStatisticsRow,
  Protocol,
} from "@app/interface/interfaces";
import { DialogService } from "./dialog.service";
import { Inject, LOCALE_ID } from "@angular/core";
import { MigrationStatusColorMap, OverallMetrics } from "@app/interface/models";
import { UtilsService } from "./utils.service";

@Injectable({
  providedIn: "root",
})
export class MigrationService {
  private apiUrl = environment.apiUrl;

  constructor(
    private dialogService: DialogService,
    private utilsService: UtilsService,
    @Inject(LOCALE_ID) private locale: string,
  ) {}

  /*Process records */

  processCompanyData(companiesData: Company[]) {
    let companies = companiesData;
    companies.forEach((company: Company) => {
      company.statusClass = this.getClassFromMigrationStatus(company.status);
    });
    return companies;
  }

  processProtocolsData(protocols: Protocol[]) {
    const cnx5Offset = 84;
    protocols.forEach((protocol: Protocol) => {
      protocol.createdOnLocal = this.getDateFromIsoString(
        protocol.created_dt as string,
        false,
      ).toString();
      protocol.irb_closure_dt_local = this.getDateFromIsoString(
        protocol.irb_closure_dt as string,
        false,
      ).toString();
      protocol.irb_approval_dt_local = this.getDateFromIsoString(
        protocol.irb_approval_dt as string,
        false,
      ).toString();
      protocol.cnx5ExternalReferenceId = protocol.cx5_study_id + cnx5Offset;
      this.updateEmptyFields(protocol);
    });
    return protocols;
  }

  processMigrationRecord(migration: Migration) {
    migration.statistics_obj = this.parseMigrationStatistics(
      migration.statistics,
    );
    migration.duration = this.getDurationInMilliseconds(
      migration.statistics_obj?.started,
      migration.statistics_obj?.ended,
    );
    migration.durationText = this.getDurationText(migration.duration);
    migration.statistics_started_local = this.getDateFromIsoString(
      migration.statistics_obj?.started,
      false,
      "Z",
    );
    migration.statistics_ended_local = this.getDateFromIsoString(
      migration.statistics_obj?.ended,
      false,
      "Z",
    );
    migration.statusCss = this.getClassFromMigrationStatus(migration.status);
    migration.created_dt_local = this.getDateFromIsoString(
      migration.created_dt as string,
      false,
      //"-08:00", // DB is stored in UTC even though db is in  PST (UTC-8)
    ).toString();
    // Normalize to ISO 8601 with PST offset for correct sorting
    migration.created_dt = this.normalizeDateToIso8601(
      migration.created_dt as string,
      //"-08:00", // DB is stored in UTC even though db is in  PST (UTC-8)
    );
    try {
      migration.progressPercentage = parseFloat(
        this.utilsService.trimEndChar(migration.progress.split(" ")[0], "%"),
      );
      migration.progressPercentageLabel = `${migration.progressPercentage}%`;
      migration.progressLabel =
        migration.progress?.split("%")[1].split(" - ")[0]?.replace("-", "") ||
        "";
      migration.statusLabel = migration.progress
        ?.split("%")[1]
        .split(" - ")[1]
        ?.replace("-", "");
      migration.exception = migration.progress
        ?.split("%")[1]
        .replace(" - ", "");
    } catch (e) {
      console.log(`processMigrationRecord error: ${e}`);
    }

    return migration;
  }

  processMigrationStatisticsRows(
    migration: Migration | null,
    sourceTimezoneOffset: string = "Z",
  ): MigrationStatisticsRow[] {
    const steps = migration?.statistics_obj?.steps || [];
    const rows: MigrationStatisticsRow[] = [];

    if (migration?.statistics) {
      // first add a row to capture the total execution
      rows.push({
        name: "Total execution",
        protocol_id: "",
        status: migration?.status || "",
        started: this.getDateFromIsoString(
          migration?.statistics_obj?.started || "",
          false,
          sourceTimezoneOffset,
        ),
        ended: this.getDateFromIsoString(
          migration?.statistics_obj?.ended || "",
          false,
          sourceTimezoneOffset,
        ),
        duration: this.getDurationInMilliseconds(
          migration?.statistics_obj?.started,
          migration?.statistics_obj?.ended,
        ),
        durationText: this.getDurationText(
          this.getDurationInMilliseconds(
            migration?.statistics_obj?.started,
            migration?.statistics_obj?.ended,
          ),
        ),
        description: "",
        startedSortValue: migration?.statistics_obj?.started || "",
      });
    }

    steps.forEach((step: MigrationExecutionStep) => {
      if (step.store_procedures && step.store_procedures.length > 0) {
        step.store_procedures.forEach(
          (procedure: MigrationExecutionStoredProcedure) => {
            rows.push({
              name: procedure.name,
              protocol_id: procedure.protocol_id,
              status: procedure.status,
              started: this.getDateFromIsoString(
                procedure.started,
                false,
                sourceTimezoneOffset,
              ),
              ended: this.getDateFromIsoString(
                procedure.ended,
                false,
                sourceTimezoneOffset,
              ),
              duration: this.getDurationInMilliseconds(
                procedure.started,
                procedure.ended,
              ),

              durationText: this.getDurationText(
                this.getDurationInMilliseconds(
                  procedure.started,
                  procedure.ended,
                ),
              ),
              description: "",
              startedSortValue: procedure.started || "",
            });
          },
        );
        return;
      }

      rows.push({
        name: step.name,
        protocol_id: "",
        status: step.status,
        started: this.getDateFromIsoString(
          step.started,
          false,
          sourceTimezoneOffset,
        ),
        ended: this.getDateFromIsoString(
          step.ended,
          false,
          sourceTimezoneOffset,
        ),
        duration: this.getDurationInMilliseconds(step.started, step.ended),
        durationText: this.getDurationText(
          this.getDurationInMilliseconds(step.started, step.ended),
        ),
        description: step.description || "",
        startedSortValue: step.started || "",
      });
    });

    return this.utilsService.sortArrayByDate(rows, "startedSortValue", "asc");
  }

  formatDateForDisplay(
    dateText: string | null | undefined,
    sourceTimezoneOffset: string = "Z",
  ): string {
    if (!dateText) {
      return "";
    }

    return this.getDateFromIsoString(dateText, false, sourceTimezoneOffset);
  }

  groupMigrationMetricsByEntityType(
    metrics: MigrationMetric[],
  ): GroupedMigrationMetrics {
    const grouped: GroupedMigrationMetrics = {};

    metrics.forEach((metric) => {
      // Initialize the entity type array if it doesn't exist
      if (!grouped[metric.entity_type]) {
        grouped[metric.entity_type] = [];
      }
      metric.migration_status = metric.migration_status
        ? metric.migration_status
        : MigrationStatus.unknown;
      // Add the metric to the array for this entity type
      metric.icon = this.getTableNameIcon(metric.entity_type);
      metric.displayData = this.getMigrationStatusDisplay(
        metric.migration_status,
      );
      grouped[metric.entity_type].push(metric);
    });

    return grouped;
  }

  processOverallMetrics(
    groupedMetrics: GroupedMigrationMetrics,
  ): OverallMetrics {
    let entityTypes = Object.keys(groupedMetrics);

    let overallMetrics = new OverallMetrics();

    entityTypes.forEach((key) => {
      groupedMetrics[key].forEach((metric) => {
        overallMetrics.totalRecords += metric.count;
        if (metric.migration_status != MigrationStatus.exception) {
          overallMetrics.succeeded += metric.count;
        } else {
          overallMetrics.failed += metric.count;
        }
      });
    });
    return overallMetrics;
  }

  /* MODALS*/
  rejectShowMigrationModal(
    company_name: string | undefined,
    onConfirm: (textFieldValue?: string) => void,
    onCancel: () => void,
  ) {
    this.dialogService.openConfirmDialog({
      enableTextField: true,
      mandatoryTextField: true,
      minlengthTextField: 5,
      rows: 3,
      title: "Reject company migration",
      message: "Are you sure you want to reject the company?",
      additionalMessage: company_name || "",
      confirmText: "Reject",
      cancelText: "Cancel",
      onConfirm: (comments) => onConfirm(comments),
      onCancel: () => onCancel(),
      confirmTextStyle: "warning-btn",
      textFieldLabel: "Comments:",
      textFieldPlaceholder:
        "Please add in a comment for a reason for rejection",
    });
  }

  public publishMigrationModal(
    company_name: string | undefined,
    onConfirm: () => void,
    onCancel: () => void,
  ) {
    this.dialogService.openConfirmDialog({
      title: "Publish migration",
      message: "Are you sure you want to publish migration for company?",
      additionalMessage: `${company_name}`,
      confirmText: "Publish",
      cancelText: "Cancel",
      onConfirm: () => onConfirm(),
      onCancel: () => onCancel(),
      confirmTextStyle: "success-btn",
    });
  }

  /***********  PRIVATE ******************/

  /* Mappings */
  private getMigrationStatusDisplay(
    status: MigrationStatus,
  ): MigrationStatusColorMap {
    switch (status) {
      case MigrationStatus.migrating:
        return new MigrationStatusColorMap(
          "accent",
          "hourglass_empty",
          "orange",
        );
      case MigrationStatus.in_progress:
        return new MigrationStatusColorMap("primary", "sync", "orange");
      case MigrationStatus.migrated:
        return new MigrationStatusColorMap("success", "check_circle", "green");
      case MigrationStatus.exception:
        return new MigrationStatusColorMap("warn", "error", "red");
      case MigrationStatus.fixed:
        return new MigrationStatusColorMap("accent", "error", "red");
      default:
        return new MigrationStatusColorMap("unclear", "lens_blur", "unclear");
    }
  }

  /**
   * Returns an appropriate Material icon for each migration database table
   * @param table The MigrationDbTables enum value
   * @returns Material icon name as string
   */
  private getTableNameIcon(table: MigrationDbTables): string {
    switch (table) {
      case MigrationDbTables.STUDY:
        return "book";

      case MigrationDbTables.USER:
        return "person";

      case MigrationDbTables.ORGANIZATION:
        return "business";

      case MigrationDbTables.ORGANIZATION_STUDY:
        return "corporate_fare";

      case MigrationDbTables.CONTACT:
        return "contact_phone";

      case MigrationDbTables.PRINCIPAL_INVESTIGATOR:
        return "group_search";

      case MigrationDbTables.SITE:
        return "location_on";

      case MigrationDbTables.STUDY_CONTACT:
        return "contact_mail";

      case MigrationDbTables.ADDRESS:
        return "home";

      case MigrationDbTables.ADDRESS_ENTITY:
        return "location_city";

      case MigrationDbTables.WORKSPACE:
        return "workspaces";

      case MigrationDbTables.FACILITY:
        return "apartment";

      case MigrationDbTables.FACILITY_LOCATION_SUBMISSION:
        return "upload_file";

      case MigrationDbTables.SITE_FACILITY:
        return "domain";

      case MigrationDbTables.DOCUMENT_TYPE:
        return "category";

      case MigrationDbTables.ORGANIZATION_TYPE:
        return "business_center";

      case MigrationDbTables.DOCUMENT:
        return "description";

      case MigrationDbTables.WORKSPACE_REVIEW:
        return "rate_review";

      case MigrationDbTables.REVIEW_ENTITY:
        return "reviews";

      case MigrationDbTables.REVIEW_HOLD:
        return "pause_circle";

      default:
        return "table_chart"; // Default icon for any unhandled cases
    }
  }

  /**
   * Normalizes backend date string to ISO 8601 format
   * Converts space separator to T for consistent parsing
   * @param dateText Raw date string from backend (e.g., '2026-03-08 03:25:24.4586100')
   * @param sourceTimezoneOffset UTC offset of the source data (e.g., '-08:00' for PST, '-07:00' for PDT, 'Z' for UTC). Defaults to 'Z'.
   * @returns ISO 8601 formatted string (e.g., '2026-03-08T03:25:24.4586100-08:00')
   */
  public normalizeDateToIso8601(
    dateText: string,
    sourceTimezoneOffset: string = "Z",
  ): string {
    if (!dateText) return "";

    // Replace space with T for ISO 8601 compliance
    let normalizedDate = dateText.replace(" ", "T");

    // If the date string doesn't have timezone info, append the source timezone offset
    if (
      !normalizedDate.includes("Z") &&
      !normalizedDate.includes("+") &&
      !normalizedDate.includes("-", 10)
    ) {
      normalizedDate = normalizedDate + sourceTimezoneOffset;
    }

    return normalizedDate;
  }

  private getDateFromIsoString(
    dateText: string,
    showMilliseconds: boolean = false,
    sourceTimezoneOffset: string = "Z",
  ): string {
    if (!dateText) return "";

    // Normalize to ISO 8601 format with the source timezone
    const dateString = this.normalizeDateToIso8601(
      dateText,
      sourceTimezoneOffset,
    );

    // Get the user's time zone
    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    return formatDate(
      new Date(dateString),
      `MMM d, y, h:mm:ss${showMilliseconds ? ".SSS" : ""} a`,
      this.locale,
      userTimeZone, // Explicitly pass the time zone
    );
  }

  private getClassFromMigrationStatus(status: MigrationStatus): string {
    switch (status) {
      case MigrationStatus.fixed:
        return "mat-success";

      case MigrationStatus.migrated:
        return "success";

      case MigrationStatus.exception:
      case MigrationStatus.fk_exception:
        return "mat-error";

      case MigrationStatus.reject:
      case MigrationStatus.double_migrated:
        return "mat-warning";
      case MigrationStatus.staged:
        return "primary";

      default:
        return "";
    }
  }

  private parseMigrationStatistics(statisticsText: string | null | undefined) {
    const emptyStatistics = { steps: [], started: "", ended: "" };

    if (!statisticsText) {
      return emptyStatistics;
    }

    try {
      return JSON.parse(statisticsText);
    } catch {
      return emptyStatistics;
    }
  }

  private getDurationInMilliseconds(
    started: string | null | undefined,
    ended: string | null | undefined,
  ): number {
    if (!started || !ended) {
      return 0;
    }

    const startedMs = new Date(started).getTime();
    const endedMs = new Date(ended).getTime();

    if (Number.isNaN(startedMs) || Number.isNaN(endedMs)) {
      return 0;
    }

    return Math.max(0, endedMs - startedMs);
  }

  private getDurationText(durationMs: number): string {
    if (!durationMs || durationMs <= 0) {
      return "0ms";
    }

    if (durationMs < 1000) {
      return `${Math.floor(durationMs)}ms`;
    }

    const totalSeconds = Math.floor(durationMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (hours > 0) {
      const parts: string[] = [`${hours}h`];
      if (minutes > 0) {
        parts.push(`${minutes}m`);
      }
      if (seconds > 0) {
        parts.push(`${seconds}s`);
      }
      return parts.join(" ");
    }

    if (minutes > 0) {
      return seconds > 0 ? `${minutes}m ${seconds}s` : `${minutes}m`;
    }

    return `${seconds}s`;
  }

  private updateEmptyFields(dataObject: any, ignoreProps: string[] = []) {
    for (const key in dataObject) {
      if (!ignoreProps.includes(key)) {
        if (dataObject[key] === "" || dataObject[key] === null) {
          dataObject[key] = "N/A";
        }
      }
    }
  }
}
